/**
 * Patient-domain pure logic — mirrored from verified backend rules so the UI
 * can explain them in words BEFORE a refused save (the server stays
 * authoritative either way).
 */

import { ApiError } from '@/core/api/errors';
import { isE164, normalizeIranMobile } from '@/core/auth/phone';
import {
  PatientErrorCode,
  type DuplicateCandidate,
  type PatientView,
  type CaregiverView,
  type RegistrationField,
} from '@/core/clinic/patientsApi';

/** Whole years between an ISO date of birth and now; null for a Guest without one. */
export function ageFromDob(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const dob = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(dob.getTime())) return null;
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const beforeBirthday =
    now.getUTCMonth() < dob.getUTCMonth() ||
    (now.getUTCMonth() === dob.getUTCMonth() && now.getUTCDate() < dob.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
}

/** SPEC02-R-11 strict boundary (verified backend rule): ≤18 or ≥65. */
export function caregiverRequired(age: number): boolean {
  return age <= 18 || age >= 65;
}

/**
 * Lifecycle presentation (ZOY-229): a Guest is a bookable identity whose
 * registration is incomplete; a Patient is a registered clinical record
 * with a file number; a merged row is an absorbed Guest (tombstone).
 */
export type PatientLifecycle = 'guest' | 'patient' | 'merged';

export function patientState(p: Pick<PatientView, 'registrationState'>): PatientLifecycle {
  if (p.registrationState === 'merged') return 'merged';
  return p.registrationState === 'registered' ? 'patient' : 'guest';
}

/** Clinical-record activity (intake, assessment, package, delivery) needs a registered Patient. */
export const isRegistered = (p: Pick<PatientView, 'registrationState'>): boolean =>
  p.registrationState === 'registered';

/** The displayed file number: never a UUID; Guests have none (ZOY-231). */
export function recordNumberLabel(
  p: Pick<PatientView, 'recordNumber'>,
  formatNumber: (n: number) => string,
): string | null {
  return p.recordNumber === null ? null : formatNumber(p.recordNumber);
}

/** Extract `missing[]` from a 409 PATIENT_REGISTRATION_INCOMPLETE body. */
export function missingRegistrationFields(err: unknown): RegistrationField[] | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code !== PatientErrorCode.RegistrationIncomplete) return null;
  const body = err.body;
  const missing =
    typeof body === 'object' && body !== null ? (body as { missing?: unknown }).missing : undefined;
  return Array.isArray(missing) ? (missing as RegistrationField[]) : [];
}

/**
 * The i18n key explaining a registration-gate refusal, or null when the
 * error is something else (callers fall through to their own mapping).
 */
export function registrationGateMessageKey(err: unknown): string | null {
  const refusal = registrationGateRefusal(err);
  if (refusal === 'not-registered') return 'pt.gate.notRegistered';
  if (refusal === 'merged') return 'pt.gate.merged';
  return null;
}

/** The registration gate refused a Guest (PATIENT_NOT_REGISTERED) or a merged tombstone. */
export function registrationGateRefusal(err: unknown): 'not-registered' | 'merged' | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  if (err.code === PatientErrorCode.NotRegistered) return 'not-registered';
  if (err.code === PatientErrorCode.Merged) return 'merged';
  return null;
}

/** Extract duplicate candidates from the 409 PATIENT_DUPLICATE_SUSPECTED body. */
export function duplicateCandidatesFrom(err: unknown): DuplicateCandidate[] | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409 || err.code !== PatientErrorCode.DuplicateSuspected) return null;
  const body = err.body;
  const candidates =
    typeof body === 'object' && body !== null
      ? (body as { candidates?: unknown }).candidates
      : undefined;
  return Array.isArray(candidates) ? (candidates as DuplicateCandidate[]) : [];
}

/** The record's operational contact: contact-first caregiver, else first live caregiver. */
export function primaryContact(caregivers: CaregiverView[] | undefined): CaregiverView | null {
  const live = (caregivers ?? []).filter((c) => c.removedAt === null);
  return live.find((c) => c.contactFirst) ?? live[0] ?? null;
}

/**
 * Classify a resolution search input. A short all-digit value (≤ 6 digits,
 * e.g. "۱۰۲۳") is a record number; a longer digit-heavy one is a phone
 * lookup; anything else is a name.
 */
export function classifyResolutionInput(raw: string): 'record' | 'phone' | 'name' | 'empty' {
  const v = raw.trim();
  if (v === '') return 'empty';
  const compact = v.replace(/\s/g, '');
  const ascii = normalizeDigits(compact);
  if (/^#?\d{1,6}$/.test(ascii)) return 'record';
  const digits = compact.replace(/[^\d۰-۹٠-٩]/g, '');
  return digits.length >= Math.ceil(compact.length / 2) && digits.length >= 3 ? 'phone' : 'name';
}

/** The integer a record-number search sends (`recordNumber eq <int>`), Persian digits normalized. */
export function recordNumberFromInput(raw: string): number | null {
  const ascii = normalizeDigits(raw.trim().replace(/\s/g, '')).replace(/^#/, '');
  if (!/^\d{1,6}$/.test(ascii)) return null;
  const n = Number(ascii);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * ZOY-336: the canonical stored form for a NEW contact mobile — E.164 via
 * the shared, tested normalizer (Auth already stores E.164). Input that
 * cannot confidently be an Iranian mobile stays digit-normalized as typed —
 * never invented.
 */
export function canonicalMobile(raw: string): string {
  const e164 = normalizeIranMobile(raw);
  return isE164(e164) ? e164 : normalizeDigits(raw.trim()).replace(/\s+/g, '');
}

/**
 * ZOY-345: the frontend's sanity gate for a mobile about to be written —
 * mirrors the backend's permissive boundary (MinLength(5); no country
 * format is imposed, by owner decision). Counts digits after Persian/Arabic
 * normalization so separators and letters don't inflate a junk value.
 */
export function mobileSanityOk(raw: string): boolean {
  return normalizeDigits(raw.trim()).replace(/\D/g, '').length >= 5;
}

/**
 * The CONTAINS-search term for a typed number (ZOY-336/346): the national
 * significant part — a substring of the E.164 form AND of a raw local
 * form, so one server query matches rows stored either way.
 */
export function mobileContainsTerm(raw: string): string {
  const e164 = normalizeIranMobile(raw);
  if (e164.startsWith('+98')) return e164.slice(3);
  return normalizeDigits(raw.trim())
    .replace(/[\s()-]/g, '')
    .replace(/^\+/, '');
}

/** Normalize Persian/Arabic digits to ASCII for machine-facing lookups. */
export function normalizeDigits(v: string): string {
  const fa = '۰۱۲۳۴۵۶۷۸۹';
  const ar = '٠١٢٣٤٥٦٧٨٩';
  return v.replace(/[۰-۹٠-٩]/g, (d) => {
    const i = fa.indexOf(d);
    return String(i >= 0 ? i : ar.indexOf(d));
  });
}

/**
 * ZOY-705: a lookup/duplicate signal's label — a known slug reads from the
 * catalogue; an unknown one falls back to a generic line, never a raw key.
 */
export function signalLabel(
  intl: { messages: Record<string, unknown>; formatMessage: (d: { id: string }) => unknown },
  sig: string,
): string {
  const id = `res.signal.${sig}`;
  return String(intl.formatMessage({ id: intl.messages[id] ? id : 'res.signal.other' }));
}
